import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  useTable,
  usePagination,
  useSortBy,
  useGlobalFilter,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faEllipsisV, faEye, faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";

/* ---------------- Global Search ---------------- */
const GlobalFilter = ({ filter, setFilter }) => (
  <input
    value={filter || ""}
    onChange={(e) => setFilter(e.target.value)}
    placeholder="Search..."
    className="form-control"
  />
);

/* ---------------- Table Component ---------------- */
const TableComponent = ({
  columns,
  data,
  globalFilter,
  setGlobalFilter,
  pageSize,
  setPageSize,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state,
    previousPage,
    nextPage,
    setGlobalFilter: internalSetFilter,
    setPageSize: internalSetPageSize,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, globalFilter, pageSize },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { pageIndex } = state;

  useEffect(() => {
    internalSetFilter(globalFilter);
  }, [globalFilter]);

  useEffect(() => {
    internalSetPageSize(pageSize);
  }, [pageSize]);

  return (
    <>
      <table className="table table-striped" {...getTableProps()} style={{ fontSize: "14px" }}>
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                  {col.render("Header")}
                  {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td
                    style={{
                      color:
                        row.original.status === "Pending"
                          ? "#0d6efd"
                          : row.original.status === "Approved"
                          ? "#198754"
                          : "inherit",
                    }}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="d-flex justify-content-between align-items-center">
        <button
          className="btn btn-secondary"
          disabled={!canPreviousPage}
          onClick={previousPage}
        >
          Previous
        </button>

        <span>
          Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
        </span>

        <button
          className="btn btn-secondary"
          disabled={!canNextPage}
          onClick={nextPage}
        >
          Next
        </button>
      </div>
    </>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */
const InventoryStatusTransferList = ({ accessOfDepartments, permissions }) => {
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState(null); // ✅ ADDED
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [globalFilter, setGlobalFilter] = useState("");
  const [pageSize, setPageSize] = useState(20);

  const code = localStorage.getItem("code");

  /* ------------ Fetch Data ------------ */
  const loadData = async () => {
    setLoading(true);

    try {
      if (statusFilter === "pending") {
        const { data } = await apiClient.get(SERVER_BASE + "/getPendingStatusTransfers", {
          params: { code, accessOfDepartments },
        });
        setTableData(data);
      } 
      else if (statusFilter === "approved") {
        const { data } = await apiClient.get(SERVER_BASE + "/getApprovedStatusTransfers", {
          params: { code, accessOfDepartments },
        });
        setTableData(data);
      } 
      else {
        const [p, a] = await Promise.all([
          apiClient.get(SERVER_BASE + "/getPendingStatusTransfers", {
            params: { code, accessOfDepartments },
          }),
          apiClient.get(SERVER_BASE + "/getApprovedStatusTransfers", {
            params: { code, accessOfDepartments },
          }),
        ]);

        setTableData([...p.data, ...a.data]);
      }
    } catch (err) {
      console.error("Error loading Status Transfers:", err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  /* ------------ Status Filter Options ------------ */
  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "all", label: "All" },
  ];

  /* ------------ Department Filter Logic (ADDED ONLY) ------------ */
  const departmentOptions = useMemo(() => {
    const deps = [...new Set(tableData.map(d => d.department).filter(Boolean))];
    return [
      { value: "all", label: "All Departments" },
      ...deps.map(d => ({ value: d, label: d })),
    ];
  }, [tableData]);

  const filteredData = useMemo(() => {
    if (!departmentFilter || departmentFilter.value === "all") {
      return tableData;
    }
    return tableData.filter(d => d.department === departmentFilter.value);
  }, [tableData, departmentFilter]);

  /* ------------ Columns ------------ */
  const columns = useMemo(
    () => [
      { Header: "S.No", Cell: ({ row }) => row.index + 1 },
      {
        Header: "Date",
        accessor: "transferDate",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleDateString("en-GB") : "",
      },
      { Header: "Ref No.", accessor: "transferCode" },
      { Header: "Department", accessor: "department" },
      { Header: "Location", accessor: "location" },
      { Header: "Added By", accessor: "createdBy" },
      { Header: "Status", accessor: "status" },
      {
        Header: "Preview",
        accessor: "Preview",
        Cell: ({ row }) =>
          permissions?.read && (
            <button
              className="btn btn-sm btn-outline-success"
              onClick={() =>
                navigate("../../previewTransferStatus", {
                  state: { id: row.original.id },
                })
              }
            >
              View
            </button>
          ),
      },
      {
        Header: "#",
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="dropdown">
              <button className="btn btn-light" data-bs-toggle="dropdown">
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  {r.createdBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPlus} /> {r.createdBy}
                      {r.createdAt &&
                        ` (${moment(r.createdAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
                <li>
                  {r.updatedBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPencil} /> {r.updatedBy}
                      {r.updatedAt &&
                        ` (${moment(r.updatedAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
                <li>
                  {r.approvedBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faCheckCircle} /> {r.approvedBy}
                      {r.approvedAt &&
                        ` (${moment(r.approvedAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
              </ul>
            </div>
          );
        },
      },
    ],
    [permissions]
  );

  return (
    <div className="formcontainer mt-5">
      <h4>Status Transfers</h4>

      <div className="row align-items-center mb-3">
        <div className="col-md-2 mb-2">
          {permissions?.write && (
            <button
              className="btn btn-outline-success w-100"
              onClick={() => navigate("../InventoryStatusTransfer")}
            >
              Add
            </button>
          )}
        </div>

        <div className="col-md-4 mb-2">
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>

        {/* ✅ DEPARTMENT FILTER ADDED */}
        <div className="col-md-2 mb-2">
          <Select
            options={departmentOptions}
            value={departmentFilter}
            onChange={setDepartmentFilter}
            placeholder="Department"
            isClearable
          />
        </div>

        <div className="col-md-2 mb-2">
          <Select
            options={statusOptions}
            value={statusOptions.find((o) => o.value === statusFilter)}
            onChange={(v) => setStatusFilter(v.value)}
          />
        </div>

        <div className="col-md-2 mb-2">
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="form-select"
          >
            {[20, 30, 50, 100].map((s) => (
              <option key={s} value={s}>
                Show {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <TableComponent
          columns={columns}
          data={filteredData}   
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}
    </div>
  );
};

export default InventoryStatusTransferList;
